import { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Plus, Trash2, Paperclip, MessageSquare, ListChecks, Calendar as CalIcon, X, Send,
  Download, FileText as FileIcon, Save, ChevronRight, Tags, Check,
} from "lucide-react";
import LabelManager from "@/components/LabelManager";
import { labelColor } from "@/lib/labelColors";

const COLUMNS = [
  { key: "open", label: "Offen", accent: "border-slate-400", dot: "bg-slate-400" },
  { key: "in_progress", label: "In Bearbeitung", accent: "border-blue-500", dot: "bg-blue-500" },
  { key: "review", label: "Prüfung", accent: "border-amber-500", dot: "bg-amber-500" },
  { key: "done", label: "Erledigt", accent: "border-emerald-500", dot: "bg-emerald-500" },
];
const PRIO = {
  high: { label: "Hoch", cls: "bg-red-50 text-red-800 border-red-200", dot: "bg-red-500" },
  medium: { label: "Mittel", cls: "bg-amber-50 text-amber-800 border-amber-200", dot: "bg-amber-500" },
  low: { label: "Niedrig", cls: "bg-emerald-50 text-emerald-800 border-emerald-200", dot: "bg-emerald-500" },
};

function fmtSize(n) {
  if (!n) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

export default function Todos() {
  const { user } = useAuth();
  const [scope, setScope] = useState("mine");
  const [items, setItems] = useState([]);
  const [labels, setLabels] = useState([]);
  const [filterLabel, setFilterLabel] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [open, setOpen] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const [draggingId, setDraggingId] = useState(null);

  const load = async () => {
    const { data } = await api.get("/todos", { params: { scope } });
    setItems(data);
  };

  const loadLabels = async () => {
    try {
      const { data } = await api.get("/labels");
      setLabels(data);
    } catch (e) { void e; }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [scope]);
  useEffect(() => { loadLabels(); }, []);
  useEffect(() => {
    if (user.role === "admin") api.get("/users").then(({ data }) => setEmployees(data)).catch(() => {});
  }, [user.role]);

  const labelsById = useMemo(() => {
    const m = {};
    labels.forEach((l) => { m[l.id] = l; });
    return m;
  }, [labels]);

  const filteredItems = useMemo(() => {
    if (!filterLabel) return items;
    return items.filter((t) => (t.label_ids || []).includes(filterLabel));
  }, [items, filterLabel]);

  const grouped = useMemo(() => {
    const g = { open: [], in_progress: [], review: [], done: [] };
    filteredItems.forEach((t) => {
      const key = g[t.status] ? t.status : "open";
      g[key].push(t);
    });
    Object.values(g).forEach((arr) => arr.sort((a, b) => (a.position ?? 0) - (b.position ?? 0)));
    return g;
  }, [filteredItems]);

  const moveTo = async (todoId, status, position = 0) => {
    // optimistic
    setItems((prev) => prev.map((t) => t.id === todoId ? { ...t, status, position } : t));
    try {
      await api.patch(`/todos/${todoId}/move`, { status, position });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Konnte nicht verschieben");
      load();
    }
  };

  const handleDragStart = (id) => setDraggingId(id);
  const handleDragEnd = () => setDraggingId(null);
  const handleDropOnColumn = (status) => {
    if (!draggingId) return;
    const dragged = items.find((t) => t.id === draggingId);
    if (!dragged) return;
    if (dragged.status === status) { setDraggingId(null); return; }
    moveTo(draggingId, status, grouped[status].length);
    setDraggingId(null);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <p className="ph-label">Aufgabenmanagement</p>
          <h1 className="font-display text-3xl font-semibold tracking-tight mt-1">Board</h1>
          <p className="text-slate-500 text-sm mt-1">Kanban-Board mit Priorität, Checklisten, Dateien und Kommentaren.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Tabs value={scope} onValueChange={setScope}>
            <TabsList>
              <TabsTrigger value="mine" data-testid="todos-tab-mine">Meine</TabsTrigger>
              <TabsTrigger value="team" data-testid="todos-tab-team">Team</TabsTrigger>
              {user.role === "admin" && <TabsTrigger value="all" data-testid="todos-tab-all">Alle</TabsTrigger>}
            </TabsList>
          </Tabs>
          <LabelManager labels={labels} onChange={loadLabels} isAdmin={user.role === "admin"} />
          {user.role === "admin" && (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button data-testid="btn-new-todo" className="bg-kauz-navy hover:bg-kauz-navy-dark h-10">
                  <Plus className="h-4 w-4 mr-2" /> Neue Aufgabe
                </Button>
              </DialogTrigger>
              <NewTodoDialog employees={employees} labels={labels} onCreated={() => { load(); setOpen(false); }} />
            </Dialog>
          )}
        </div>
      </div>

      {/* Label filter bar */}
      {labels.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap" data-testid="label-filter-bar">
          <span className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">Filter:</span>
          <button
            onClick={() => setFilterLabel(null)}
            data-testid="filter-all"
            className={`text-xs px-3 py-1 rounded-full border transition ${
              !filterLabel ? "bg-slate-900 border-slate-900 text-white"
                           : "bg-white border-slate-300 text-slate-600 hover:bg-slate-50"
            }`}
          >
            Alle
          </button>
          {labels.map((l) => {
            const c = labelColor(l.color);
            const active = filterLabel === l.id;
            return (
              <button
                key={l.id}
                onClick={() => setFilterLabel(active ? null : l.id)}
                data-testid={`filter-label-${l.id}`}
                className={`inline-flex items-center gap-1.5 text-xs px-3 py-1 rounded-full border transition ${
                  active ? `${c.bg} ${c.border} ${c.text} ring-2 ring-offset-1 ring-sky-600`
                         : `${c.bg} ${c.border} ${c.text} hover:opacity-80`
                }`}
              >
                <span className={`h-1.5 w-1.5 rounded-full ${c.dot}`} />
                {l.name}
              </button>
            );
          })}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        {COLUMNS.map((col) => (
          <Column
            key={col.key}
            col={col}
            cards={grouped[col.key]}
            labelsById={labelsById}
            isDropTarget={!!draggingId}
            onDrop={() => handleDropOnColumn(col.key)}
            onCardClick={(id) => setActiveId(id)}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
          />
        ))}
      </div>

      {activeId && (
        <TodoDetailDialog
          todoId={activeId}
          employees={employees}
          labels={labels}
          onClose={() => setActiveId(null)}
          onChanged={load}
          currentUser={user}
        />
      )}
    </div>
  );
}

function Column({ col, cards, labelsById, isDropTarget, onDrop, onCardClick, onDragStart, onDragEnd }) {
  const [over, setOver] = useState(false);
  return (
    <div
      data-testid={`col-${col.key}`}
      onDragOver={(e) => { e.preventDefault(); setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => { e.preventDefault(); setOver(false); onDrop(); }}
      className={`rounded-xl border-t-[3px] ${col.accent} bg-white border border-slate-200 p-3 min-h-[200px] transition ${
        isDropTarget ? "ring-1 ring-slate-200" : ""
      } ${over ? "bg-kauz-navy/5 ring-2 ring-kauz-navy/30" : ""}`}
    >
      <div className="flex items-center justify-between mb-3 px-1">
        <h3 className="font-display text-sm font-semibold text-slate-800 flex items-center gap-2">
          <span className={`h-2 w-2 rounded-full ${col.dot}`} />
          {col.label}
        </h3>
        <Badge variant="outline" className="border-slate-200 bg-slate-50 text-slate-600 text-[10px] h-5">
          {cards.length}
        </Badge>
      </div>
      <div className="space-y-2">
        {cards.map((card) => (
          <Card
            key={card.id}
            card={card}
            labelsById={labelsById}
            onClick={() => onCardClick(card.id)}
            onDragStart={() => onDragStart(card.id)}
            onDragEnd={onDragEnd}
          />
        ))}
        {cards.length === 0 && (
          <div className="text-xs text-slate-400 px-2 py-6 text-center border border-dashed border-slate-200 rounded-md">
            Keine Aufgaben
          </div>
        )}
      </div>
    </div>
  );
}

function Card({ card, labelsById, onClick, onDragStart, onDragEnd }) {
  const checklist = card.checklist || [];
  const checklistDone = checklist.filter((c) => c.done).length;
  const prio = PRIO[card.priority] || PRIO.medium;
  const isOverdue = card.due_date && card.status !== "done" && new Date(card.due_date) < new Date(new Date().toDateString());
  const cardLabels = (card.label_ids || []).map((id) => labelsById?.[id]).filter(Boolean);

  return (
    <button
      type="button"
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onClick={onClick}
      data-testid={`todo-card-${card.id}`}
      className="block w-full text-left ph-card p-3 hover:shadow-md cursor-grab active:cursor-grabbing transition group"
    >
      {cardLabels.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-2">
          {cardLabels.slice(0, 4).map((l) => {
            const c = labelColor(l.color);
            return (
              <span key={l.id} title={l.name}
                className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase tracking-wider border ${c.bg} ${c.border} ${c.text}`}>
                <span className={`h-1.5 w-1.5 rounded-full ${c.dot}`} />
                {l.name}
              </span>
            );
          })}
          {cardLabels.length > 4 && <span className="text-[9px] text-slate-500">+{cardLabels.length - 4}</span>}
        </div>
      )}
      <div className="flex items-start gap-2 mb-1.5">
        <span className={`h-2 w-2 rounded-full ${prio.dot} mt-1.5 flex-shrink-0`} />
        <h4 className="text-sm font-medium text-slate-900 line-clamp-2 flex-1">{card.title}</h4>
      </div>
      {card.description && (
        <p className="text-xs text-slate-500 line-clamp-2 mb-2">{card.description}</p>
      )}
      <div className="flex items-center justify-between gap-2 text-[11px] text-slate-500">
        <div className="flex items-center gap-2.5">
          {card.due_date && (
            <span className={`inline-flex items-center gap-1 ${isOverdue ? "text-red-700 font-semibold" : ""}`}>
              <CalIcon className="h-3 w-3" />
              {new Date(card.due_date).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" })}
            </span>
          )}
          {checklist.length > 0 && (
            <span className={`inline-flex items-center gap-1 ${checklistDone === checklist.length ? "text-emerald-700" : ""}`}>
              <ListChecks className="h-3 w-3" />
              {checklistDone}/{checklist.length}
            </span>
          )}
          {card.comments_count > 0 && (
            <span className="inline-flex items-center gap-1">
              <MessageSquare className="h-3 w-3" /> {card.comments_count}
            </span>
          )}
          {card.attachments_count > 0 && (
            <span className="inline-flex items-center gap-1">
              <Paperclip className="h-3 w-3" /> {card.attachments_count}
            </span>
          )}
        </div>
        <span className="text-[10px] uppercase tracking-wider font-semibold truncate max-w-[110px]">
          {card.assignee_id ? (card.assignee_name || "—") : "Team"}
        </span>
      </div>
    </button>
  );
}

function NewTodoDialog({ employees, onCreated }) {
  const [form, setForm] = useState({ title: "", description: "", due_date: "", priority: "medium", assignee_id: "TEAM" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        title: form.title, description: form.description, priority: form.priority,
        due_date: form.due_date || null,
        assignee_id: form.assignee_id === "TEAM" ? null : form.assignee_id,
      };
      await api.post("/todos", payload);
      toast.success("Aufgabe erstellt");
      onCreated();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler");
    } finally {
      setBusy(false);
    }
  };
  return (
    <DialogContent className="max-w-lg">
      <DialogHeader><DialogTitle className="font-display">Neue Aufgabe</DialogTitle></DialogHeader>
      <form onSubmit={submit} className="space-y-4">
        <Field label="Titel *">
          <Input data-testid="todo-title" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
        </Field>
        <Field label="Beschreibung">
          <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Fälligkeitsdatum">
            <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
          </Field>
          <Field label="Priorität">
            <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
              <SelectTrigger data-testid="todo-priority"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="low">Niedrig</SelectItem>
                <SelectItem value="medium">Mittel</SelectItem>
                <SelectItem value="high">Hoch</SelectItem>
              </SelectContent>
            </Select>
          </Field>
        </div>
        <Field label="Zuweisen an">
          <Select value={form.assignee_id} onValueChange={(v) => setForm({ ...form, assignee_id: v })}>
            <SelectTrigger data-testid="todo-assignee"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="TEAM">Gesamtes Team</SelectItem>
              {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
        <DialogFooter>
          <Button type="submit" data-testid="todo-submit" disabled={busy} className="bg-kauz-navy hover:bg-kauz-navy-dark">
            {busy ? "Erstellen…" : "Aufgabe erstellen"}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}

function TodoDetailDialog({ todoId, employees, labels, onClose, onChanged, currentUser }) {
  const [todo, setTodo] = useState(null);
  const [comments, setComments] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [newChecklist, setNewChecklist] = useState("");
  const [editingMeta, setEditingMeta] = useState(false);
  const [meta, setMeta] = useState({});
  const [labelPopOpen, setLabelPopOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const isAdmin = currentUser.role === "admin";

  const refresh = async () => {
    const [t, c, a] = await Promise.all([
      api.get(`/todos/${todoId}`),
      api.get(`/todos/${todoId}/comments`),
      api.get(`/todos/${todoId}/attachments`),
    ]);
    setTodo(t.data);
    setComments(c.data);
    setAttachments(a.data);
    setMeta({
      title: t.data.title,
      description: t.data.description || "",
      due_date: t.data.due_date || "",
      priority: t.data.priority,
      assignee_id: t.data.assignee_id || "TEAM",
    });
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [todoId]);

  if (!todo) return null;

  const checklist = todo.checklist || [];
  const checklistDone = checklist.filter((c) => c.done).length;
  const progress = checklist.length > 0 ? Math.round((checklistDone / checklist.length) * 100) : 0;
  const prio = PRIO[todo.priority] || PRIO.medium;
  const colLabel = COLUMNS.find((c) => c.key === todo.status)?.label || "Offen";

  const saveMeta = async () => {
    setBusy(true);
    try {
      await api.patch(`/todos/${todoId}`, {
        title: meta.title,
        description: meta.description,
        due_date: meta.due_date || null,
        priority: meta.priority,
        assignee_id: meta.assignee_id === "TEAM" ? "" : meta.assignee_id,
      });
      toast.success("Gespeichert");
      setEditingMeta(false);
      await refresh();
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler");
    } finally {
      setBusy(false);
    }
  };

  const moveStatus = async (status) => {
    try {
      await api.patch(`/todos/${todoId}/move`, { status, position: 0 });
      await refresh();
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler");
    }
  };

  const addComment = async () => {
    if (!newComment.trim()) return;
    try {
      await api.post(`/todos/${todoId}/comments`, { text: newComment });
      setNewComment("");
      await refresh();
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler");
    }
  };

  const delComment = async (cid) => {
    try {
      await api.delete(`/todos/${todoId}/comments/${cid}`);
      await refresh();
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler");
    }
  };

  const addChecklist = async () => {
    if (!newChecklist.trim()) return;
    try {
      await api.post(`/todos/${todoId}/checklist`, { text: newChecklist });
      setNewChecklist("");
      await refresh();
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler");
    }
  };

  const toggleChecklist = async (item) => {
    try {
      await api.patch(`/todos/${todoId}/checklist/${item.id}`, { done: !item.done });
      await refresh();
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler");
    }
  };

  const delChecklist = async (id) => {
    try {
      await api.delete(`/todos/${todoId}/checklist/${id}`);
      await refresh();
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler");
    }
  };

  const uploadFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/todos/${todoId}/attachments`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Datei hochgeladen");
      if (fileRef.current) fileRef.current.value = "";
      await refresh();
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload fehlgeschlagen");
    }
  };

  const downloadAttachment = async (a) => {
    try {
      const res = await api.get(`/todos/${todoId}/attachments/${a.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: a.content_type }));
      const link = document.createElement("a");
      link.href = url; link.download = a.filename; document.body.appendChild(link); link.click(); link.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Download fehlgeschlagen");
    }
  };

  const delAttachment = async (id) => {
    if (!window.confirm("Datei wirklich löschen?")) return;
    try {
      await api.delete(`/todos/${todoId}/attachments/${id}`);
      await refresh();
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler");
    }
  };

  const delTodo = async () => {
    if (!window.confirm("Aufgabe komplett löschen?")) return;
    try {
      await api.delete(`/todos/${todoId}`);
      toast.success("Aufgabe gelöscht");
      onClose();
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler");
    }
  };

  const toggleLabel = async (labelId) => {
    const current = todo.label_ids || [];
    const next = current.includes(labelId) ? current.filter((x) => x !== labelId) : [...current, labelId];
    // optimistic
    setTodo({ ...todo, label_ids: next });
    try {
      await api.put(`/todos/${todoId}/labels`, { label_ids: next });
      await refresh();
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler");
      await refresh();
    }
  };

  const todoLabels = (todo.label_ids || []).map((id) => labels.find((l) => l.id === id)).filter(Boolean);

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="todo-detail">
        <DialogHeader>
          <div className="flex items-start gap-3">
            <span className={`h-3 w-3 rounded-full ${prio.dot} mt-2.5 flex-shrink-0`} />
            <div className="flex-1 min-w-0">
              {editingMeta ? (
                <Input value={meta.title} onChange={(e) => setMeta({ ...meta, title: e.target.value })}
                  className="text-xl font-semibold h-10" />
              ) : (
                <DialogTitle className="font-display text-xl">{todo.title}</DialogTitle>
              )}
              <div className="text-xs text-slate-500 mt-1 flex items-center gap-2 flex-wrap">
                <span>in <strong>{colLabel}</strong></span>
                <span>·</span>
                <Badge variant="outline" className={`${prio.cls} text-[10px] uppercase`}>{prio.label}</Badge>
                {todo.assignee_id ? (
                  <span>· an {todo.assignee_name}</span>
                ) : (
                  <span>· Team</span>
                )}
                {todo.due_date && <span>· fällig {new Date(todo.due_date).toLocaleDateString("de-DE")}</span>}
              </div>
            </div>
          </div>
        </DialogHeader>

        {/* Labels */}
        <section className="space-y-2">
          <div className="flex items-center justify-between">
            <h4 className="ph-label inline-flex items-center gap-1.5">
              <Tags className="h-3.5 w-3.5" /> Labels
            </h4>
            <button onClick={() => setLabelPopOpen((v) => !v)} className="text-xs text-kauz-navy hover:text-kauz-navy-dark inline-flex items-center gap-1" data-testid="btn-labels-toggle">
              <Plus className="h-3 w-3" /> {labelPopOpen ? "Schließen" : "Labels zuweisen"}
            </button>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {todoLabels.length === 0 && !labelPopOpen && (
              <span className="text-xs text-slate-400 italic">Keine Labels zugewiesen</span>
            )}
            {todoLabels.map((l) => {
              const c = labelColor(l.color);
              return (
                <button key={l.id} onClick={() => toggleLabel(l.id)} data-testid={`label-chip-${l.id}`}
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-semibold uppercase tracking-wider border ${c.bg} ${c.border} ${c.text} hover:opacity-80 group`}>
                  <span className={`h-1.5 w-1.5 rounded-full ${c.dot}`} />
                  {l.name}
                  <X className="h-3 w-3 opacity-50 group-hover:opacity-100" />
                </button>
              );
            })}
          </div>
          {labelPopOpen && (
            <div className="border border-slate-200 rounded-md p-3 bg-slate-50/60 space-y-1.5">
              <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-1">Verfügbar</div>
              {labels.length === 0 && (
                <p className="text-xs text-slate-400 italic">Noch keine Labels angelegt. Verwende „Labels"-Button im Board-Header.</p>
              )}
              <div className="flex flex-wrap gap-1.5">
                {labels.map((l) => {
                  const assigned = (todo.label_ids || []).includes(l.id);
                  const c = labelColor(l.color);
                  return (
                    <button key={l.id} onClick={() => toggleLabel(l.id)}
                      data-testid={`label-toggle-${l.id}`}
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-medium border transition ${
                        assigned ? `${c.bg} ${c.border} ${c.text} ring-2 ring-offset-1 ring-sky-600`
                                 : `${c.bg} ${c.border} ${c.text} opacity-60 hover:opacity-100`
                      }`}>
                      <span className={`h-1.5 w-1.5 rounded-full ${c.dot}`} />
                      {l.name}
                      {assigned && <Check className="h-3 w-3" />}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </section>

        {/* Status quick switcher */}
        <div className="flex flex-wrap gap-1.5">
          {COLUMNS.map((c) => (
            <button
              key={c.key}
              onClick={() => moveStatus(c.key)}
              data-testid={`move-${c.key}`}
              className={`text-xs px-3 py-1.5 rounded-full border transition ${
                todo.status === c.key
                  ? "bg-slate-900 border-slate-900 text-white"
                  : "bg-white border-slate-300 text-slate-700 hover:bg-slate-50"
              }`}
            >
              {c.label} {todo.status === c.key && <ChevronRight className="inline h-3 w-3 ml-0.5" />}
            </button>
          ))}
        </div>

        {/* Description */}
        <section className="space-y-2">
          <div className="flex items-center justify-between">
            <h4 className="ph-label">Beschreibung</h4>
            {isAdmin && !editingMeta && (
              <button onClick={() => setEditingMeta(true)} className="text-xs text-kauz-navy hover:text-kauz-navy-dark">
                Bearbeiten
              </button>
            )}
          </div>
          {editingMeta ? (
            <div className="space-y-3">
              <Textarea value={meta.description} onChange={(e) => setMeta({ ...meta, description: e.target.value })} rows={4} />
              <div className="grid grid-cols-3 gap-2">
                <Input type="date" value={meta.due_date}
                  onChange={(e) => setMeta({ ...meta, due_date: e.target.value })} />
                <Select value={meta.priority} onValueChange={(v) => setMeta({ ...meta, priority: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Niedrig</SelectItem>
                    <SelectItem value="medium">Mittel</SelectItem>
                    <SelectItem value="high">Hoch</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={meta.assignee_id} onValueChange={(v) => setMeta({ ...meta, assignee_id: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="TEAM">Gesamtes Team</SelectItem>
                    {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-2">
                <Button size="sm" onClick={saveMeta} disabled={busy} className="bg-kauz-navy hover:bg-kauz-navy-dark">
                  <Save className="h-3.5 w-3.5 mr-1.5" /> Speichern
                </Button>
                <Button size="sm" variant="outline" onClick={() => setEditingMeta(false)}>Abbrechen</Button>
              </div>
            </div>
          ) : (
            <p className="text-sm text-slate-700 whitespace-pre-wrap">
              {todo.description || <span className="text-slate-400 italic">Keine Beschreibung</span>}
            </p>
          )}
        </section>

        {/* Checklist */}
        <section className="space-y-2">
          <div className="flex items-center justify-between">
            <h4 className="ph-label inline-flex items-center gap-1.5">
              <ListChecks className="h-3.5 w-3.5" /> Checkliste
              {checklist.length > 0 && (
                <span className="ml-1 text-[10px] text-slate-500">({checklistDone}/{checklist.length})</span>
              )}
            </h4>
          </div>
          {checklist.length > 0 && (
            <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
              <div className="h-full bg-emerald-500 transition-all" style={{ width: `${progress}%` }} />
            </div>
          )}
          <ul className="space-y-1.5">
            {checklist.map((item) => (
              <li key={item.id} className="flex items-center gap-2 group" data-testid={`check-${item.id}`}>
                <button onClick={() => toggleChecklist(item)}
                  className={`h-5 w-5 rounded-md border-2 grid place-items-center flex-shrink-0 transition ${
                    item.done ? "bg-emerald-600 border-emerald-600" : "border-slate-300 hover:border-sky-600"
                  }`}>
                  {item.done && <svg className="h-3 w-3 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4"><path d="M5 12l5 5L20 7" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                </button>
                <span className={`text-sm flex-1 ${item.done ? "line-through text-slate-400" : "text-slate-700"}`}>
                  {item.text}
                </span>
                <button onClick={() => delChecklist(item.id)}
                  className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-600 transition">
                  <X className="h-3.5 w-3.5" />
                </button>
              </li>
            ))}
          </ul>
          <form onSubmit={(e) => { e.preventDefault(); addChecklist(); }} className="flex gap-2">
            <Input value={newChecklist} onChange={(e) => setNewChecklist(e.target.value)}
              placeholder="Neuer Eintrag…" className="h-9" data-testid="check-new" />
            <Button type="submit" size="sm" variant="outline" className="h-9"><Plus className="h-3.5 w-3.5" /></Button>
          </form>
        </section>

        {/* Attachments */}
        <section className="space-y-2">
          <h4 className="ph-label inline-flex items-center gap-1.5">
            <Paperclip className="h-3.5 w-3.5" /> Dateien ({attachments.length})
          </h4>
          {attachments.length > 0 && (
            <ul className="space-y-1.5">
              {attachments.map((a) => (
                <li key={a.id} className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-md px-3 py-2">
                  <FileIcon className="h-4 w-4 text-slate-500 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-slate-800 truncate">{a.filename}</div>
                    <div className="text-[10px] text-slate-500">{fmtSize(a.size)} · {a.uploaded_by_name}</div>
                  </div>
                  <button onClick={() => downloadAttachment(a)} className="h-7 w-7 grid place-items-center rounded hover:bg-white text-slate-600">
                    <Download className="h-3.5 w-3.5" />
                  </button>
                  {(isAdmin || a.uploaded_by_id === currentUser.id) && (
                    <button onClick={() => delAttachment(a.id)} className="h-7 w-7 grid place-items-center rounded hover:bg-red-50 text-red-600">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
          <div>
            <input ref={fileRef} type="file" className="hidden" onChange={uploadFile} data-testid="todo-file-input" />
            <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} className="h-9">
              <Paperclip className="h-3.5 w-3.5 mr-1.5" /> Datei anhängen
            </Button>
          </div>
        </section>

        {/* Comments */}
        <section className="space-y-2">
          <h4 className="ph-label inline-flex items-center gap-1.5">
            <MessageSquare className="h-3.5 w-3.5" /> Kommentare ({comments.length})
          </h4>
          <ul className="space-y-2">
            {comments.map((c) => (
              <li key={c.id} className="flex gap-2.5 group" data-testid={`comment-${c.id}`}>
                <div className="h-7 w-7 rounded-full bg-gradient-to-br from-sky-700 to-teal-700 grid place-items-center text-white text-xs font-semibold flex-shrink-0">
                  {c.author_name?.[0]?.toUpperCase() || "?"}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-xs font-medium text-slate-800">{c.author_name}</span>
                    <span className="text-[10px] text-slate-400">{new Date(c.created_at).toLocaleString("de-DE")}</span>
                  </div>
                  <p className="text-sm text-slate-700 whitespace-pre-wrap bg-slate-50 border border-slate-200 rounded-md px-3 py-2">{c.text}</p>
                </div>
                {(isAdmin || c.author_id === currentUser.id) && (
                  <button onClick={() => delComment(c.id)}
                    className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-600 transition self-start mt-1">
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </li>
            ))}
          </ul>
          <form onSubmit={(e) => { e.preventDefault(); addComment(); }} className="flex gap-2">
            <Textarea value={newComment} onChange={(e) => setNewComment(e.target.value)}
              placeholder="Kommentar schreiben…" rows={2} data-testid="comment-new" />
            <Button type="submit" size="sm" className="bg-kauz-navy hover:bg-kauz-navy-dark self-end" data-testid="comment-submit">
              <Send className="h-3.5 w-3.5" />
            </Button>
          </form>
        </section>

        {isAdmin && (
          <DialogFooter className="border-t pt-3 mt-2">
            <Button variant="outline" onClick={delTodo} className="text-red-700 border-red-200 hover:bg-red-50" data-testid="todo-delete">
              <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Aufgabe löschen
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">{label}</Label>
      {children}
    </div>
  );
}
